import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Copy, Search, Star } from 'lucide-react';
import { AudienceLevel, LEVEL_LABELS, LEVEL_ORDER, PathwayTemplate } from '../../types/pathway';
import { moduleById } from '../../data/skillModules';
import { formatHours, totalHours } from '../../utils/pathwayMatching';
import { SelectDropdown } from '../SelectDropdown';
import { ResetFiltersButton } from '../ResetFiltersButton';
interface ProvenPathwaysProps {
  templates: PathwayTemplate[];
  onUseTemplate: (template: PathwayTemplate) => void;
}

/** Adoption expressed out of 5, relative to the most-used pathway. */
function adoptionStars(organizationsUsing: number, max: number) {
  if (max <= 0) return 0;
  return Math.max(1, Math.round(organizationsUsing / max * 5));
}
function AdoptionRating({
  stars,
  orgs
}: {
  stars: number;
  orgs: number;
}) {
  return <span className="flex items-center gap-1.5" title={`Used by ${orgs} organizations`} data-id="element-1737" data-mp="347izc0bjylqv">
      <span className="flex" aria-hidden="true" data-id="element-1738" data-mp="kj3766ggl8us">
        {[1, 2, 3, 4, 5].map(step => <Star key={step} size={13} className={step <= stars ? 'fill-[#30718D] text-[#30718D]' : 'text-gray-300'} data-id="element-1739" data-mp="omj3f1om976y" />)}
      </span>
      <span className="text-xs text-gray-500" data-id="element-1740" data-mp="fmmhh7tm2mfl">{orgs}</span>
      <span className="sr-only" data-id="element-1741" data-mp="13bovm5p1dx5c">
        Used by {orgs} organizations, {stars} out of 5 for adoption
      </span>
    </span>;
}

/**
 * Pre-built sequences anyone can start from. Deliberately kept outside the
 * saved-pathways folder so removing that feature leaves these intact.
 */
export function ProvenPathways({
  templates,
  onUseTemplate
}: ProvenPathwaysProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [audienceFilter, setAudienceFilter] = useState('all');
  const [sort, setSort] = useState('used');
  const maxOrgs = useMemo(() => Math.max(...templates.map(t => t.organizationsUsing), 0), [templates]);
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = templates.filter(template => {
      if (audienceFilter !== 'all' && template.audience !== audienceFilter) return false;
      if (needle && !`${template.name} ${template.summary}`.toLowerCase().includes(needle)) {
        return false;
      }
      return true;
    });
    return filtered.slice().sort((a, b) => {
      if (sort === 'audience') {
        return LEVEL_ORDER.indexOf(a.audience) - LEVEL_ORDER.indexOf(b.audience) || a.name.localeCompare(b.name);
      }
      if (sort === 'longest') return totalHours(b.moduleIds) - totalHours(a.moduleIds);
      if (sort === 'shortest') return totalHours(a.moduleIds) - totalHours(b.moduleIds);
      if (sort === 'size') return b.moduleIds.length - a.moduleIds.length;
      return b.organizationsUsing - a.organizationsUsing;
    });
  }, [templates, query, audienceFilter, sort]);
  const isFiltered = query.trim().length > 0 || audienceFilter !== 'all' || sort !== 'used';
  const resetFilters = () => {
    setQuery('');
    setAudienceFilter('all');
    setSort('used');
  };
  return <section aria-labelledby="proven-pathways-heading" className="mt-10" data-id="element-1742" data-mp="3jsimftovd0g8">
      <div className="mb-5" data-id="element-1743" data-mp="2n5x8glb556uj">
        <h2 id="proven-pathways-heading" className="text-xl font-bold text-gray-900" data-id="element-1744" data-mp="2n2j6vfaax1o5">
          Proven pathways
        </h2>
        <p className="mt-1 max-w-xl text-sm text-gray-600" data-id="element-1745" data-mp="2w3ze5e5tkno5">
          Pre-built sequences your team already sells. Duplicate one and edit it
          for your prospect.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3" data-id="element-1746" data-mp="23fbrpf9c3s90">
        <label className="relative" data-id="element-1747" data-mp="28fg52wey46v0">
          <span className="sr-only" data-id="element-1748" data-mp="28eene93fcm4b">Search proven pathways</span>
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1749" data-mp="ewh9uenmaqj2" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search proven pathways" className="w-64 rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1750" data-mp="9z7e6h9mxims" />
        </label>
        <SelectDropdown ariaLabel="Filter proven pathways by audience" scrollMenu value={audienceFilter} onChange={setAudienceFilter} options={[{
        value: 'all',
        label: 'All audiences'
      }, ...LEVEL_ORDER.map((level: AudienceLevel) => ({
        value: level,
        label: LEVEL_LABELS[level]
      }))]} data-id="element-1751" data-mp="sn2m3kikg27z" />
        <SelectDropdown ariaLabel="Sort proven pathways" scrollMenu value={sort} onChange={setSort} options={[{
        value: 'used',
        label: 'Most used by organizations'
      }, {
        value: 'audience',
        label: 'Audience level'
      }, {
        value: 'longest',
        label: 'Longest duration'
      }, {
        value: 'shortest',
        label: 'Shortest duration'
      }, {
        value: 'size',
        label: 'Most modules'
      }]} data-id="element-1752" data-mp="34f9aueqrqjhx" />
        <ResetFiltersButton isActive={isFiltered} onReset={resetFilters} data-id="element-1753" data-mp="2akzalscwlpjx" />
      </div>

      {visible.length === 0 ? <div className="rounded-xl border border-gray-100 bg-white px-6 py-10 text-center shadow-sm" data-id="element-1754" data-mp="2fm4zff3g0so6">
          <p className="text-sm text-gray-600" data-id="element-1755" data-mp="21n5eoij1wxxa">
            No proven pathways match those filters.
          </p>
          <button type="button" onClick={resetFilters} className="mt-2 text-sm font-medium text-[#30718D] transition-colors hover:text-[#265a70]" data-id="element-1756" data-mp="26m04w5jclorf">
            Clear filters
          </button>
        </div> : <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm" data-id="element-1757" data-mp="2fozfau0lxrx">
          <div className="overflow-x-auto" data-id="element-1758" data-mp="3uh4w9nvyudkg">
            <table className="w-full table-fixed sm:min-w-[860px]" data-id="element-1759" data-mp="3pitr3qgm8r8k">
              <caption className="sr-only" data-id="element-1760" data-mp="3pj3gbbtu6ud7">
                Proven pathways — expand a row to see its modules
              </caption>
              <colgroup data-id="element-1761" data-mp="m1v1z1ybicn8">
                <col data-id="element-1762" data-mp="m2e9rpu97knj" />
                <col className="w-[200px]" data-id="element-1763" data-mp="m1la18tppvew" />
                <col className="w-[110px]" data-id="element-1764" data-mp="h31kyzjto3ib" />
                <col className="w-[130px]" data-id="element-1765" data-mp="h4cqp4wtvlc2" />
                <col className="w-[140px]" data-id="element-1766" data-mp="h5e4vi954ny0" />
              </colgroup>
              <thead data-id="element-1767" data-mp="caqjvtdcibo6">
                <tr className="border-b border-gray-200 bg-gray-50" data-id="element-1768" data-mp="c9yq8o3b6o0j">
                  <th className="whitespace-nowrap px-3 py-3 text-left text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1769" data-mp="7cp3jg7b0qge">
                    Pathway
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-left text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1770" data-mp="101m9qky8qwss">
                    Audience
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-center text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1771" data-mp="1cc58resapytq">
                    Modules
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-left text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1772" data-mp="1h9x0o8sxxkxx">
                    Used by
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-right text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1773" data-mp="1qav6mwmxw2j5">
                    <span className="sr-only" data-id="element-1774" data-mp="1v9ersutplaag">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1775" data-mp="203sqp77kxzjv">
                {visible.map(template => {
              const expanded = expandedId === template.id;
              return <React.Fragment key={template.id} data-id="element-1776" data-mp="2a0x8kuue78uv">
                      <tr className="border-b border-gray-100 transition-colors hover:bg-gray-50" data-id="element-1777" data-mp="2e1h3rsveo2ed">
                        <td className="px-3 py-4 sm:px-6" data-id="element-1778" data-mp="2zcfjvhalmeqe">
                          <button type="button" onClick={() => setExpandedId(expanded ? null : template.id)} aria-expanded={expanded} className="flex items-start gap-2 text-left" data-id="element-1779" data-mp="2zaut82renfxt">
                            <ChevronDown size={16} aria-hidden="true" className={`mt-0.5 flex-shrink-0 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} data-id="element-1780" data-mp="2gmzg8l3ma65r" />
                            <span data-id="element-1781" data-mp="1xvgfkkx2f46w">
                              <span className="block text-sm font-semibold text-gray-900" data-id="element-1782" data-mp="1xuetzmifazax">
                                {template.name}
                              </span>
                              <span className="mt-0.5 block text-xs text-gray-500" data-id="element-1783" data-mp="8eku8ncrcke4">
                                {template.summary}
                              </span>
                            </span>
                          </button>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700 sm:px-6" data-id="element-1784" data-mp="3vgxve6kmc6bc">
                          {LEVEL_LABELS[template.audience]}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-center text-sm text-gray-700 sm:px-6" data-id="element-1785" data-mp="r5lrn6y8c7vk">
                          {template.moduleIds.length}
                          <span className="ml-1 text-xs text-gray-500" data-id="element-1786" data-mp="i32un3h4kph8">
                            · {formatHours(totalHours(template.moduleIds))}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 sm:px-6" data-id="element-1787" data-mp="32tk52rbfe907">
                          <AdoptionRating stars={adoptionStars(template.organizationsUsing, maxOrgs)} orgs={template.organizationsUsing} data-id="element-1788" data-mp="37tpzgf92y9lz" />
                        </td>
                        <td className="px-3 py-4 sm:px-6" data-id="element-1789" data-mp="m17aq56al7hb">
                          <div className="flex items-center justify-end" data-id="element-1790" data-mp="qzqvi295gd83">
                            <button type="button" onClick={() => onUseTemplate(template)} className="flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-[#30718D] transition-colors hover:text-[#265a70]" data-id="element-1791" data-mp="c3jd97haxlvg">
                              <Copy size={14} data-id="element-1792" data-mp="82hbojzwwxqb" />
                              Duplicate
                            </button>
                          </div>
                        </td>
                      </tr>

                      <AnimatePresence initial={false} data-id="element-1793" data-mp="22f77x8dr6j0v">
                        {expanded && <tr data-id="element-1794" data-mp="2ck5p3csr54jg">
                            <td colSpan={5} className="border-b border-gray-100 bg-white p-0" data-id="element-1795" data-mp="2ch9plk19a7sw">
                              <motion.div initial={{
                        opacity: 0,
                        height: 0
                      }} animate={{
                        opacity: 1,
                        height: 'auto'
                      }} exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.2,
                        ease: [0.23, 1, 0.32, 1]
                      }} className="overflow-hidden" data-id="element-1796" data-mp="2le29on69q8wr">
                                <ol className="my-2 ml-6 space-y-1.5 border-l-2 border-[#30718D]/20 pl-4 pr-3 sm:ml-10 sm:pr-6" data-id="element-1797" data-mp="19wiz29qcz3o6">
                                  {template.moduleIds.map((moduleId, index) => {
                            const module = moduleById(moduleId);
                            if (!module) return null;
                            return <li key={moduleId} className="flex items-baseline gap-3 text-sm" data-id="element-1798" data-mp="3ey506ub76zfi" data-mpi={index}>
                                        <span className="w-4 flex-shrink-0 text-xs font-bold text-[#30718D]" data-id="element-1799" data-mp="3nzl90xmd6thm">
                                          {index + 1}
                                        </span>
                                        <span className="font-medium text-gray-900" data-id="element-1800" data-mp="1k9ztsbm9klx">
                                          {module.name}
                                        </span>
                                        <span className="text-xs text-gray-500" data-id="element-1801" data-mp="bi7mibdtv9cf">
                                          {formatHours(module.durationHours)} ·{' '}
                                          {module.category}
                                        </span>
                                      </li>;
                          })}
                                </ol>
                              </motion.div>
                            </td>
                          </tr>}
                      </AnimatePresence>
                    </React.Fragment>;
            })}
              </tbody>
            </table>
          </div>
        </div>}
    </section>;
}